import React, { useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { cx } from '@/utils/strings'
import { createPortal } from 'react-dom'

interface SimpleTooltipProps {
  className?: string
  content: React.ReactNode
  children: React.ReactNode
  backgroundColor?: 'Black' | 'White'
}

export default function SimpleTooltip({ content, children, className, backgroundColor = 'White' }: SimpleTooltipProps) {
  const [isVisible, setIsVisible] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const tooltipPosition = useRef({ top: 0, left: 0 })

  const showTooltip = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect()
      tooltipPosition.current = { top: window.scrollY + rect.y, left: rect.x + rect.width / 2 }
      setIsVisible(true)
    }
  }

  return (
    <div
      ref={ref}
      className="relative inline-block"
      onMouseEnter={() => showTooltip()}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible &&
        createPortal(
          <div
            className="absolute z-10 -translate-y-full -translate-x-1/2"
            style={{ top: tooltipPosition.current.top, left: tooltipPosition.current.left }}
          >
            <motion.div
              className="mb-2"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ ease: 'anticipate' }}
            >
              <div
                className={cx(
                  'relative px-6 py-4 rounded-lg',
                  backgroundColor === 'Black' && 'bg-secondary-900 text-white dark:bg-white dark:text-secondary-900',
                  backgroundColor === 'White' && 'bg-white text-black shadow-lg border-t-[1px] border-secondary-50 dark:bg-secondary-900 dark:text-white dark:border-secondary-700',
                  className
                )}
              >
                {content}
                <div
                  className={cx(
                    'absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[8px]',
                    backgroundColor === 'Black' && 'border-t-secondary-900 dark:border-t-white',
                    backgroundColor === 'White' && 'border-t-white dark:border-t-secondary-900'
                  )}
                />
              </div>
            </motion.div>
          </div>,
          document.body
        )}
    </div>
  )
}
